/**
 * Per-role tab + sidebar visibility — single source of truth shared
 * by mobile (bottom tabs + project-detail tab strip) and web
 * (sidebar + project-detail tab strip).
 *
 * Why this lives in `@shared/perms`:
 *   Both surfaces have to agree on what each role can SEE. Mobile
 *   uses `useVisibleProjectTabs()` to filter the project-detail tab
 *   strip; web's `ProjectDetail.tsx` and `App.tsx` sidebar use the
 *   same map so an Accountant on web sees only Transactions + Party
 *   (the same two tabs they see on mobile), not all 9.
 *
 *  When the role-access matrix changes (docs/roles-and-permissions.md),
 *  update the maps below — mobile and web both pick it up.
 */
import type { RoleKey } from './roles';

// ── Bottom-tab visibility (mobile) ────────────────────────────────

export type BottomTabKey =
  | 'index'
  | 'overview'
  | 'crm'
  | 'toolkit'
  | 'account'
  /** Client-only finance tab. Hosts the CONTRACT / PAID / PENDING
   *  summary card and the list of receipted payments. */
  | 'payment';

/** Bottom-tab routes a given role can see. SA / Admin / Manager /
 *  Viewer get CRM; Accountant / Site Engineer / Supervisor have no
 *  CRM tab; Designer has no Finance + no CRM; Client gets a narrow
 *  Payment tab in place of Overview + CRM. */
export const BOTTOM_TABS_BY_ROLE: Record<RoleKey, ReadonlySet<BottomTabKey>> = {
  superAdmin:   new Set(['index', 'overview', 'crm', 'toolkit', 'account']),
  admin:        new Set(['index', 'overview', 'crm', 'toolkit', 'account']),
  manager:      new Set(['index', 'overview', 'crm', 'toolkit', 'account']),
  // Viewer — full read role, but no `finance.read` capability, so
  // the Overview (Finance) bottom tab renders a locked screen. Drop
  // it to avoid the dead-end UX. Viewer keeps CRM (read leads /
  // appointments) and the rest.
  viewer:       new Set(['index', 'crm', 'toolkit', 'account']),
  accountant:   new Set(['index', 'overview', 'toolkit', 'account']),
  siteEngineer: new Set(['index', 'overview', 'toolkit', 'account']),
  supervisor:   new Set(['index', 'overview', 'toolkit', 'account']),
  client:       new Set(['index', 'payment', 'toolkit', 'account']),
  designer:     new Set(['index', 'toolkit', 'account']),
};

export const EMPTY_BOTTOM_TABS: ReadonlySet<BottomTabKey> = new Set();

/** Pure helper. Returns the right tab set for a role, or empty
 *  while loading. UI hooks layer the React `loading` state on top. */
export function visibleBottomTabs(
  role: RoleKey | null | undefined,
): ReadonlySet<BottomTabKey> {
  if (!role) return EMPTY_BOTTOM_TABS;
  return BOTTOM_TABS_BY_ROLE[role] ?? EMPTY_BOTTOM_TABS;
}

// ── Project-detail-tab visibility (mobile + web) ──────────────────

export type ProjectTabKey =
  | 'transaction'
  | 'site'
  | 'task'
  | 'attendance'
  | 'material'
  | 'party'
  | 'laminate'
  | 'files';

export const PROJECT_TABS_BY_ROLE: Record<RoleKey, ReadonlySet<ProjectTabKey>> = {
  superAdmin: new Set([
    'transaction', 'site', 'task', 'attendance',
    'material', 'party', 'laminate', 'files',
  ]),
  admin: new Set([
    'transaction', 'site', 'task', 'attendance',
    'material', 'party', 'laminate', 'files',
  ]),
  manager: new Set([
    'transaction', 'site', 'task', 'attendance',
    'material', 'party', 'laminate', 'files',
  ]),
  // Accountant — money + party tabs only.
  accountant: new Set(['transaction', 'party']),
  // Site Engineer — full execution stack, no Party.
  siteEngineer: new Set([
    'transaction', 'site', 'task', 'attendance',
    'material', 'laminate', 'files',
  ]),
  // Supervisor — on-site daily-execution surface + transaction
  // submit-only (filtered to own-submissions inside TransactionTab).
  // Laminate / Files are READ-ONLY for supervisors —
  // they need to reference design files + laminate selections to
  // execute the build correctly. Write CTAs inside those tabs are
  // already gated by canEditTasks (Supervisor excluded), so adding
  // the tabs only opens the reading surface.
  supervisor: new Set([
    'transaction', 'site', 'task', 'attendance', 'material',
    'laminate', 'files',
  ]),
  // Viewer — full read across every tab.
  viewer: new Set([
    'transaction', 'site', 'task', 'attendance',
    'material', 'party', 'laminate', 'files',
  ]),
  // Client — read-only on a curated subset (no money / no team).
  client: new Set(['site', 'task', 'files', 'laminate']),
  // Designer — design-stack only.
  designer: new Set(['site', 'task', 'laminate', 'files']),
};

export const EMPTY_PROJECT_TABS: ReadonlySet<ProjectTabKey> = new Set();

/** Pure helper. Returns the right project-tab set for a role. */
export function visibleProjectTabs(
  role: RoleKey | null | undefined,
): ReadonlySet<ProjectTabKey> {
  if (!role) return EMPTY_PROJECT_TABS;
  return PROJECT_TABS_BY_ROLE[role] ?? EMPTY_PROJECT_TABS;
}

// ── Web sidebar visibility ────────────────────────────────────────

/** Sidebar nav keys for the web customer app. Each one corresponds
 *  to a top-level route in `web-customer/src/App.tsx` NAV_GROUPS.
 *
 *  These mirror the mobile tabs where they overlap, plus the web-
 *  only library + admin routes. Filtering by role here keeps
 *  Accountant from seeing nav items they can't write to. */
export type SidebarNavKey =
  | 'projects'
  /** Org-wide read of transactions + recent activity. Viewer has
   *  this even though they don't have `finance` (no Finance hub /
   *  Ledger access) — Dashboard is the read-only summary they CAN
   *  see via `canReadProjectTransactions`. */
  | 'dashboard'
  | 'finance'
  | 'leads'
  | 'calendar'
  | 'quotations'
  | 'tasks'
  | 'team'
  | 'parties'
  | 'staff'
  | 'materials'
  | 'services'
  | 'categories'
  | 'platform_services'
  | 'profile'
  | 'plans'
  | 'billing';

/** Which sidebar items each role sees in the web customer app.
 *
 *  Read-only access still surfaces some items (Tasks, Materials,
 *  Services, Categories) because viewing them is useful — the
 *  per-page write-CTAs are gated separately so Accountant sees the
 *  page but no "Add" button. */
export const SIDEBAR_BY_ROLE: Record<RoleKey, ReadonlySet<SidebarNavKey>> = {
  superAdmin: new Set([
    'projects', 'dashboard', 'finance', 'leads', 'calendar', 'quotations', 'tasks',
    'team', 'parties', 'staff', 'materials', 'services', 'categories',
    'platform_services', 'profile', 'plans', 'billing',
  ]),
  admin: new Set([
    'projects', 'dashboard', 'finance', 'leads', 'calendar', 'quotations', 'tasks',
    'team', 'parties', 'staff', 'materials', 'services', 'categories',
    'platform_services', 'profile',
  ]),
  manager: new Set([
    'projects', 'dashboard', 'finance', 'leads', 'calendar', 'quotations', 'tasks',
    'team', 'parties', 'staff', 'materials', 'services', 'categories',
    'platform_services', 'profile',
  ]),
  // Accountant — money + party only on the project side; no CRM,
  // no tasks-write so we still let them browse Tasks read-only but
  // hide Leads / Calendar / Quotations / Materials / Services from
  // the nav (they can't write to any of them — surfacing them is
  // just clutter).
  accountant: new Set([
    'projects', 'dashboard', 'finance', 'parties', 'staff', 'profile',
  ]),
  siteEngineer: new Set([
    'projects', 'tasks', 'parties', 'materials', 'profile',
  ]),
  supervisor: new Set([
    'projects', 'tasks', 'parties', 'materials', 'profile',
  ]),
  // Viewer — full read, but no `finance.read`. They see Dashboard
  // (which uses transaction.read they DO have) but NOT Finance /
  // Ledger (those hard-lock Viewer). Splitting `dashboard` and
  // `finance` into separate navKeys avoids the dead-end nav items.
  viewer: new Set([
    'projects', 'dashboard', 'leads', 'calendar', 'quotations', 'tasks',
    'team', 'parties', 'staff', 'materials', 'services', 'categories',
    'profile',
  ]),
  // Client — projects only (their own).
  client: new Set(['projects', 'profile']),
  // Designer — design-stack nav only.
  designer: new Set([
    'projects', 'tasks', 'materials', 'profile',
  ]),
};

export const EMPTY_SIDEBAR: ReadonlySet<SidebarNavKey> = new Set();

export function visibleSidebar(
  role: RoleKey | null | undefined,
): ReadonlySet<SidebarNavKey> {
  if (!role) return EMPTY_SIDEBAR;
  return SIDEBAR_BY_ROLE[role] ?? EMPTY_SIDEBAR;
}
